import { ChangeDetectionStrategy, Component, computed, effect, inject, input, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ReactiveContainer } from '../../../core/containers/reactive-container';
import { ActivityStreamComponent } from '../../../core/components/activity-stream/activity-stream.component';
import { WidgetCoordinatorService } from '../../../core/services/issue/widget-coordinator.service';
import { SIDEBAR_CONTEXT } from '../../../core/services/sidebar/sidebar-context';
import { CommandInput } from '../../../core/entities/commandTemplate/commandTemplate';
import { CommandService, Reloadable } from '../../../core/services/command/command.service';
import { RefetchCoordinator } from '../../../core/services/refetch-coordinator/refetch-coordinator.service';
import { Task } from 'src/app/rent-module/shared/entities/task/task';
import * as fromGraphQl from './graphql';
import { MenuItem } from 'primeng/api';

@Component({
  selector: 'app-task-view',
  templateUrl: './task-view.component.html',
  styleUrls: ['./task-view.component.scss'],
  providers: [WidgetCoordinatorService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.in-sidebar]': 'isInSidebar'
  }
})
export class TaskViewComponent implements OnInit, OnDestroy, Reloadable {

  // View children
  readonly activityStream = viewChild(ActivityStreamComponent);

  // Input parameters
  showBreadcrumb = input<boolean>(true);

  // Injected dependencies
  private readonly route = inject(ActivatedRoute);
  readonly commandService = inject(CommandService);
  private readonly refetchCoordinator = inject(RefetchCoordinator);
  
  // Sidebar context - true if this component is rendered inside a sidebar
  readonly isInSidebar = inject(SIDEBAR_CONTEXT, { optional: true }) ?? false;

  // Container for GraphQL query (composition over inheritance)
  private readonly container = new ReactiveContainer<fromGraphQl.CompleteTaskQuery>(
    fromGraphQl.COMPLETE_TASK_QUERY,
    { stopPollingOnSuccess: true }
  );

  // Route param
  private id = '';

  // Refetch coordinator unregister function
  private unregisterRefetch?: () => void;

  // Signals for reactive state - task is derived from container.data
  readonly task = computed(() => {
    const data = this.container.data();
    return data?.task ? new Task(data.task) : null;
  });
  protected readonly createdByName = computed(() => {
    const task = this.task();
    return `${task?.createdBy?.firstName ?? ''} ${task?.createdBy?.lastName ?? ''}`.trim();
  });
  readonly showDescription = computed(() => {
    const task = this.task();
    return task?.isDefaultTask() ?? false;
  });

  // Menu items computed from task commands
  readonly menuItems = computed<MenuItem[]>(() => {
    const task = this.task();
    if (!task?.commands?.length) return [];
    
    return task.commands.map((command) => ({
      label: command.settings?.label,
      disabled: command.settings?.disabled,
      command: () => {
        this.commandService.execute(task, command, this);
      }
    }));
  });

  readonly loading = this.container.loading;
  readonly error = this.container.error;

  readonly menuVisible = signal(false);
  readonly isEditMode = signal(false);
  readonly titleOverride = signal<string | null>(null);

  // Computed values - automatically update when task changes
  readonly updateCommandInput = computed(() => {
    const task = this.task();
    if (!task) return undefined;
    
    const commandInput = new CommandInput();
    commandInput.name = 'taskUpdateCommand';
    commandInput.addParameter('commandProviderId', task.id);
    return commandInput;
  });

  // Signal-based form group - reactive to edit mode and task changes
  readonly taskFormGroup = computed(() => {
    if (!this.isEditMode()) return null;
    const task = this.task();
    return new FormGroup({
      title: new FormControl(task?.title, [Validators.required]),
      description: new FormControl(task?.description)
    });
  });

  // Edit mode callbacks
  private onSave: ((data: any) => void) | undefined;
  private onCancel: (() => void) | undefined;

  private static readonly COMMAND_INPUTS = [
    { name: 'taskDeleteCommand' },
    { name: 'rentalResourceAssignmentTaskSplitCompositTaskCommand' },
    { name: 'rentalResourceAssignmentTaskComposeAtomicTaskCommand' }
  ];

  ngOnInit(): void {
    // Support both /tasks/task/:id and /tasks?show=:id
    this.id = this.route.snapshot.params['id'] || this.route.snapshot.queryParams['show'];
    this.container.execute(this.getVariables());

    // Register as detail view with entity ID
    this.unregisterRefetch = this.refetchCoordinator.register(this, Task.ENTITY_TYPE, this.id);
  }

  ngOnDestroy(): void {
    this.unregisterRefetch?.();
  }

  reload(): void {
    this.container.refetch(this.getVariables());
  }

  private getVariables(): { id: string; commandInputs: { name: string }[] } {
    return {
      id: this.id,
      commandInputs: TaskViewComponent.COMMAND_INPUTS
    };
  }

  enterEditMode(): Promise<{ action: 'save' | 'cancel'; data?: any }> {
    return new Promise((resolve) => {
      this.isEditMode.set(true);
      
      this.onSave = (data: any) => {
        this.isEditMode.set(false);
        resolve({ action: 'save', data });
      };
      
      this.onCancel = () => {
        this.isEditMode.set(false);
        resolve({ action: 'cancel' });
      };
    });
  }

  saveChanges(): void {
    this.onSave?.(this.taskFormGroup()?.value);
  }

  cancelChanges(): void {
    this.onCancel?.();
  }

  onMenuShow(): void {
    this.menuVisible.set(true);
  }

  onMenuHide(): void {
    this.menuVisible.set(false);
  }

  onUpdateTitle(title: string | null): void {
    console.log("Title override: " + title);
    this.titleOverride.set(title);
  }
}
